/** @jsxImportSource @emotion/react */
import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { DataGrid, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import type { ChatQuestion } from '@trinity/chat';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { AnalysisQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ActivityIndicator } from '../presentation/ActivityIndicator';
import { Constants } from '../Theme';
import {
  messageLaneRightPadding,
  messageLaneWidth,
  messageLaneLeftPadding,
} from './Message.styles';
import { BasicSystemUserMessage } from './Message.System.Basic';

type Props = unknown;

export const QuestionsBank = memo((props: Props) => {
  const theme = useTheme();
  const brandIdentifier = useActiveBrandIdentifier();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const apiClient = useChatApiClient();

  const q = useQuery<ChatQuestion[]>({
    queryKey: ['chat-questions', brandIdentifier],
    queryFn: () => {
      return apiClient.chat.getChatQuestions({ brandIdentifier }).then((res) => res.questions);
    },
  });

  const dispatch = useContext(ChatStateDispatchContext);

  const handleQuestionClick = useCallback(
    (value: string) => {
      dispatch({
        type: 'input-changed',
        payload: { value },
      });
    },
    [dispatch],
  );

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'category',
      headerName: 'Topic Category',
      width: 150,
      editable: false,
    },
    {
      field: 'topic',
      headerName: 'Topic',
      width: 150,
      editable: false,
    },
    {
      field: 'question',
      headerName: 'Question',
      flex: 1,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <AnalysisQueryHint
          value={params.value as string}
          onClick={() => handleQuestionClick(params.value as string)}
        />
      ),
    },
  ], [handleQuestionClick]);

  const rows = useMemo(() => {
    if (!q.data) return [];
    return q.data.map((item, index) => ({
      id: index,
      category: item.category,
      topic: item.topic,
      question: item.question,
    }));
  }, [q.data]);

  if (q.isLoading) {
    return (
      <MessageContainer>
        <BasicSystemUserMessage
          bodyStyles={css({
            minWidth: messageLaneWidth,
            maxWidth: messageLaneWidth,
          })}
        >
          <LoadingWrap>
            <ActivityIndicator color={theme.palette.loader.main} />
          </LoadingWrap>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }

  return (
    <MessageContainer>
      <BasicSystemUserMessage
        bodyStyles={css({
          minWidth: messageLaneWidth,
          maxWidth: messageLaneWidth,
        })}
      >
        <DataGridContainer>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pagination
            pageSizeOptions={[5, 10, 25]}
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                whiteSpace: 'normal !important',
                wordWrap: 'break-word !important',
              },
              '& .MuiDataGrid-columnHeader': {
                fontWeight: 'bold !important',
              },
            }}
          />
        </DataGridContainer>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
});

// function getQuestions(): QuestionItem[] {
//   const profileQuestions = [
//     `I'd like to learn more about the publications this HCP has appeared in.`,
//     `I'd like to learn more about the clinical trials this HCP participated in.`,
//     `I'd like to learn more about this HCP's conference presence.`,
//     `I'd like to learn more about publications where this HCP was the lead author.`,
//     `Please summarize this HCP's experience.`,
//     `Are there any specific journals where this HCP frequently publishes?`,
//     `Are there any notable collaborations mentioned in this HCP's publications?`,
//     `What are some themes I could engage this HCP on?`,
//   ];

//   return [
//     ...profileQuestions.map((value) => ({
//       topic: 'HCP 360 Profile',
//       category: 'HCP Attributes',
//       value,
//     })),
//     {
//       topic: 'MSL Observations & Questions',
//       category: 'Voice of Customer',
//       value: 'Give me recent notes from last conversation',
//     },
//     {
//       topic: 'Best Visit Time',
//       category: 'Voice of Customer',
//       value:
//         'When is this HCP typically avialable for meetings based on past visit patterns?',
//     },
//     {
//       topic: 'Best Visit Time',
//       category: 'Voice of Customer',
//       value:
//         'Suggest the best time to schedule a visit with this HCP next week',
//     },
//     {
//       topic: 'HCP Visit Routing',
//       category: 'Voice of Customer',
//       value:
//         'Can you generate an optimized route to visit HCPs in my territory this week?',
//     },
//     {
//       topic: 'HCP Visit Routing',
//       category: 'Voice of Customer',
//       value:
//         'Which HCPs should I prioritize for in-person vists based on proximity?',
//     },
//     {
//       topic: 'HCP Interaction Trends',
//       category: 'Voice of Customer',
//       value:
//         'Can you show a chart of all logged interactions with this HCP over the past 3 months?',
//     },
//     {
//       topic: 'HCP Interaction Trends',
//       category: 'Voice of Customer',
//       value:
//         'Chart the decline scores for digital topics this HCP contributes to',
//     },
//   ];
// }

// type QuestionItem = {
//   category: string;
//   topic: string;
//   value: string;
// };

const MessageContainer = styled.div({
  paddingLeft: messageLaneLeftPadding,
  paddingRight: messageLaneRightPadding,
});

const DataGridContainer = styled.div({
  height: '500px',
  width: '100%',
});

const StyledDataGrid = styled(DataGrid)`
  & .MuiDataGrid-cell {
    white-space: normal !important;
    word-wrap: break-word !important;
    padding: 8px;
  }
  & .MuiDataGrid-columnHeader {
    font-weight: bold !important;
    background-color: #f5f5f5;
  }
  & .MuiDataGrid-row:hover {
    background-color: #f9f9f9;
  }
`;

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 35px;
  box-sizing: border-box;
  margin: 0 ${Constants.Sidebar.ItemHorizontalPadding}px;
`;
